import { useState, useCallback } from "react";

export interface ReviewItem {
  classId: number;
  classTitle: string;
  completedAtMs: number;
  nextReviewMs: number;
  stage: number;
}

const INTERVAL_DAYS = [1, 3, 7];

interface ReviewStore {
  items: ReviewItem[];
}

const DEFAULT: ReviewStore = { items: [] };

function load(): ReviewStore {
  try {
    const raw = localStorage.getItem("growseo_reviews");
    if (raw) return JSON.parse(raw);
  } catch (e) {
    console.warn("ReviewStore load error", e);
  }
  return DEFAULT;
}

function save(s: ReviewStore) {
  try {
    localStorage.setItem("growseo_reviews", JSON.stringify(s));
  } catch (e) {
    console.warn("ReviewStore save error", e);
  }
}

export function useReviewStore() {
  const [data, setData] = useState<ReviewStore>(load);

  const scheduleReview = useCallback((classId: number, classTitle: string, completedAtMs: number) => {
    setData((prev) => {
      const existing = prev.items.find((i) => i.classId === classId);
      if (existing) return prev;
      const item: ReviewItem = {
        classId,
        classTitle,
        completedAtMs,
        nextReviewMs: completedAtMs + INTERVAL_DAYS[0] * 24 * 60 * 60 * 1000,
        stage: 0,
      };
      const next = { items: [item, ...prev.items] };
      save(next);
      return next;
    });
  }, []);

  const markReviewed = useCallback((classId: number) => {
    setData((prev) => {
      const items = prev.items.map((item) => {
        if (item.classId !== classId) return item;
        const nextStage = item.stage + 1;
        if (nextStage >= INTERVAL_DAYS.length) {
          return { ...item, stage: INTERVAL_DAYS.length };
        }
        return {
          ...item,
          stage: nextStage,
          nextReviewMs: Date.now() + INTERVAL_DAYS[nextStage] * 24 * 60 * 60 * 1000,
        };
      });
      const next = { items };
      save(next);
      return next;
    });
  }, []);

  const dueToday = data.items.filter(
    (i) => i.stage < INTERVAL_DAYS.length && i.nextReviewMs <= Date.now()
  );

  const upcomingItems = data.items.filter(
    (i) => i.stage < INTERVAL_DAYS.length && i.nextReviewMs > Date.now()
  );

  return { items: data.items, dueToday, upcomingItems, scheduleReview, markReviewed };
}
